import React, { useEffect, useState } from "react";
import { Card, notification, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { ILicenseDetails } from "@Models/ILicenseDetails";
import LicenseService from "@Services/LicenseService";
import LicenseEditor from "@Components/licenses/LicenseEditor";
import "@Styles/components/licenses.scss";
import LargeSpinner from "@Components/shared/LargeSpinner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@Constants";
import SessionManager from "@Core/session";
import { IActivityDefinitions } from "@Models/IActivityDetails";
import GuidanceDrawer from "@Components/shared/GuidanceDrawer";
import { getTutorialTip } from "@Components/assessment/helpers";

interface DataType extends ILicenseDetails {
    key: React.Key;
}

const LicensesTab = () => {
    const [current, setCurrent] = useState<ILicenseDetails>(null);
    const [rowIndex, setRowIndex] = useState<number>(null);
    const [licenses, setLicenses] = useState<ILicenseDetails[]>([]);
    const queryClient = useQueryClient();
    const definitions = queryClient.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);

    const { isFetching, data } = useQuery(
        [QueryKeys.AdminLicenses],
        async () => {
            const licenseService = new LicenseService();
            const result = await licenseService.get();
            if (!result.hasErrors) {
                if (result.value.length > 0) {
                    return result.value;
                }
            } else {
                notification.error({ message: "Error retrieving licenses." });
            }
            return [];
        },
        {
            enabled: !!SessionManager.getServiceUser()?.userId,
            refetchOnWindowFocus: false,
        }
    );

    const updateCurrent = (license: ILicenseDetails) => {
        setCurrent(license);
    };

    const deleteCurrent = (license: ILicenseDetails) => {
        setCurrent(null);
        setRowIndex(null);
        const newLicenses = licenses.filter((x) => x.licenseId !== license.licenseId);
        setLicenses(newLicenses);
    };

    const columns: ColumnsType<DataType> = [
        {
            title: "Customer",
            dataIndex: "licensee",
            key: "licensee",
            sorter: (a, b) => {
                if (a.licensee > b.licensee) {
                    return 1;
                }
                if (a.licensee < b.licensee) {
                    return -1;
                }
                return 0;
            },
        },
        {
            title: "License",
            dataIndex: "name",
            key: "name",
        },

        {
            title: "Product",
            dataIndex: "productId",
            key: "productId",
            render: (productId: string) => (
                <span>{definitions?.products?.find((x) => x.productId === productId)?.label}</span>
            ),
        },
        {
            title: "Capacity",
            dataIndex: "capacity",
            key: "capacity",
            render: (data: number) => <span>{data === 0 ? "Unlimited" : data}</span>,
        },
        {
            title: "Renewal",
            dataIndex: "endDate",
            key: "endDate",
            render: (date: string) => <span>{moment(date).format("MMM Do YYYY")}</span>,
            sorter: (a, b) => {
                return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
            },
        },
    ];

    const selectRow = (license: ILicenseDetails, index: number) => {
        setCurrent(license);
        setRowIndex(index);
    };

    useEffect(() => {
        if (data?.length > 0) {
            setLicenses(data);
            if (current == null) {
                setCurrent(data[0]);
            } else {
                setCurrent(data.find((x) => x.licenseId === current.licenseId));
            }
        }
    }, [data]);

    return (
        <>
            <div className="guidance-container">
                <GuidanceDrawer text={getTutorialTip("licenses")} />
            </div>
            <Card className="licenses-tab newUI-raised">
                {isFetching && data === undefined ? (
                    <LargeSpinner />
                ) : (
                    <section className="licenses-row">
                        <div className={`license-table ${current ? "edit" : ""}`}>
                            <Table
                                rowClassName={(x, i) => "selectable-row" + (i === rowIndex ? " selected" : "")}
                                onRow={(x, i) => ({
                                    onClick: () => {
                                        selectRow(x, i);
                                    },
                                })}
                                bordered
                                dataSource={licenses}
                                columns={columns}
                                pagination={{ pageSize: 10, position: ["bottomCenter"] }}
                            />
                        </div>
                        <div className={`license-edit ${current ? "edit" : ""}`}>
                            <LicenseEditor
                                fetching={isFetching}
                                licenses={licenses}
                                current={current}
                                updateCurrent={updateCurrent}
                                deleteCurrent={deleteCurrent}
                            />
                        </div>
                    </section>
                )}
            </Card>
        </>
    );
};

export default LicensesTab;
